import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { deleteUserFromGroup, sendTIMLaunchEmails, sendTIMReminderEmails, updateTimRol, updateUser, FRONT_URL } from "../../axios/axiosFunctions";
import { roleLabel, slugForCategory } from "../../utils/timFamily";

const defaultUser = {
    firstName: "",
    lastName:"",
    email: "",
    type: "",
}

const TIMActions = ({row,api,category}) => {

    const navigate = useNavigate();
    const [openDialogEdit, setOpenDialogEdit] = useState(false);
    const [openDialogDelete, setOpenDialogDelete] = useState(false);
    const [openDialogEmail, setOpenDialogEmail] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailResult, setEmailResult] = useState(null);
    const [alertSeverity, setAlertSeverity] = useState("error");
    const [alertMsg, setAlertMsg] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [userForm, setUserForm] = useState(defaultUser);
    const { firstName, lastName, email, type } = userForm;

    useEffect(() => {
        initUser();
    }, [])

    const initUser = ()=>{
        setUserForm({
            firstName: row.firstName || "",
            lastName: row.lastName || "",
            email: row.email || "",
            type: row.type || "member",
        });
    }

    const handleCloseDialogEdit = ()=>{
        setOpenDialogEdit(false);
    }

    const handleCloseDialogDelete = ()=>{
        setOpenDialogDelete(false);
    }

    const handleEdit = ()=>{
        initUser();
        setOpenDialogEdit(true);
    }

    const handleOpenDelete = ()=>{
        setOpenDialogDelete(true);
    }

    const handleChange = (e)=>{
        const {name,value} = e.target;
        setUserForm({...userForm,[name]:value});
    }

    const handleCancelEdit = ()=>{
        initUser();
        handleCloseDialogEdit();
    }
    
    const submitUser = async()=>{
        const dataUser = {
            firstName,
            lastName,
            email
        }

        const dataRol = {
            rol:type
        }

        const resUser = await updateUser(row.userId,dataUser);
        
        if(!resUser.user){
            setAlertMsg(resUser.msg);
            setAlertSeverity("error");
            setAlertOpen(true);
            setTimeout(() => {
              setAlertOpen(false);
            }, 2000);
            return;
        }

        await updateTimRol(row.userId,row.groupId,dataRol);
        api.updateRows([{id:row.id,firstName,lastName,email,type}]);
        handleCloseDialogEdit();
    }

    const handleSendEmail = async (kind) => {
        setSendingEmail(true);
        setEmailResult(null);
        try {
            const data = { frontUrl: FRONT_URL, memberIds: [row.userId] };
            const res = kind === "reminder"
                ? await sendTIMReminderEmails(row.groupId, data)
                : await sendTIMLaunchEmails(row.groupId, data);
            if (res?.error) {
                setEmailResult({ severity: "error", msg: res.msg || "Failed to send email." });
            } else if (kind === "reminder") {
                if (res?.skippedCompleted > 0) {
                    setEmailResult({ severity: "info", msg: `${row.firstName} ${row.lastName} has already completed the assessment.` });
                } else if (res?.remindersSent > 0) {
                    setEmailResult({ severity: "success", msg: `Reminder sent to ${row.firstName} ${row.lastName} (${row.email}).` });
                } else {
                    setEmailResult({ severity: "warning", msg: "No reminder was sent." });
                }
            } else {
                if (res?.skippedCompleted > 0) {
                    setEmailResult({ severity: "info", msg: `${row.firstName} ${row.lastName} has already completed the assessment.` });
                } else if (res?.sent > 0) {
                    setEmailResult({ severity: "success", msg: `Launch email sent to ${row.firstName} ${row.lastName} (${row.email}).` });
                } else {
                    setEmailResult({ severity: "warning", msg: "Launch email was not sent." });
                }
            }
        } catch (err) {
            setEmailResult({ severity: "error", msg: "Failed to send email. Please try again." });
        }
        setSendingEmail(false);
    };

    const handleCloseEmailDialog = () => {
        if (sendingEmail) return;
        setOpenDialogEmail(false);
        setEmailResult(null);
    };

    const deleteUser = async()=>{
        const res = await deleteUserFromGroup(row.groupId,row.userId);
        if(!res){
            setAlertMsg("Error while deleting, please try later or contact an admin");
            setAlertSeverity("error");
            setAlertOpen(true);
            setTimeout(() => {
              setAlertOpen(false);
            }, 2000);
            return;
        }
        row.updateTable();
        row.callGroups();
        handleCloseDialogDelete();
    }

    const memberCompleted = row.status === "Completed";

    return (
    <Box sx={{width: "100%",height: "100%",display: "flex",justifyContent: "space-evenly", alignItems: "center"}}>
        {row.status && row.status !== "Not started" && (
            <Tooltip title="See assessment responses">
                <IconButton
                    onClick={() => navigate(`/responses/${slugForCategory(category)}/${row.groupId}/${row.userId}`)}
                    size="small"
                >
                    <VisibilityIcon sx={{color:"#0F5385",fontSize:"20px"}}/>
                </IconButton>
            </Tooltip>
        )}
        <Tooltip title="Send launch or reminder email to this member">
            <IconButton onClick={() => setOpenDialogEmail(true)} size="small">
                <EmailIcon sx={{color:"#1976d2",fontSize:"20px"}}/>
            </IconButton>
        </Tooltip>
        <IconButton onClick={handleEdit}>
            <EditIcon sx={{color:"orange"}}/>
        </IconButton>
        <IconButton onClick={handleOpenDelete}>
            <DeleteIcon sx={{color:"red"}}/>
        </IconButton>
        <Dialog open={openDialogEmail} onClose={handleCloseEmailDialog}>
            <DialogTitle sx={{ fontWeight: "bold" }}>Send Email</DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 2 }}>
                    Send email to <strong>{row.firstName} {row.lastName}</strong> ({row.email})
                </Typography>
                {memberCompleted && !emailResult && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        This member has already completed the assessment.
                    </Alert>
                )}
                {emailResult && (
                    <Alert severity={emailResult.severity} sx={{ mt: 1 }}>{emailResult.msg}</Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: "16px", flexDirection: "column", gap: 1, alignItems: "stretch" }}>
                {!emailResult ? (
                    <>
                        <Button
                            onClick={() => handleSendEmail("launch")}
                            variant="contained"
                            color="primary"
                            disabled={sendingEmail || memberCompleted}
                            startIcon={sendingEmail ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}
                        >
                            Send Launch Email
                        </Button>
                        <Button
                            onClick={() => handleSendEmail("reminder")}
                            variant="outlined"
                            color="warning"
                            disabled={sendingEmail || memberCompleted}
                            startIcon={sendingEmail ? <CircularProgress size={18} color="inherit" /> : <NotificationsActiveIcon />}
                        >
                            Send Reminder Email
                        </Button>
                        <Button onClick={handleCloseEmailDialog} disabled={sendingEmail} color="inherit">
                            Cancel
                        </Button>
                    </>
                ) : (
                    <Button onClick={handleCloseEmailDialog} variant="contained">Done</Button>
                )}
            </DialogActions>
        </Dialog>
        <Dialog open={openDialogEdit} onClose={handleCloseDialogEdit}>
            <DialogContent>
                <Typography variant="h5" color="primary">EDIT</Typography>
                <Grid container spacing={2}>
                    <Grid size={6}>
                        <Typography variant="subtitle1">First Name</Typography>
                        <TextField onChange={handleChange} value={firstName} name="firstName" fullWidth/>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="subtitle1">Last Name</Typography>
                        <TextField onChange={handleChange} value={lastName} name="lastName" fullWidth/>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="subtitle1">Email</Typography>
                        <TextField onChange={handleChange} value={email} name="email" fullWidth/>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="subtitle1">Type</Typography>
                        <FormControl sx={{width:"100%"}}>
                            <Select onChange={handleChange} value={type} name="type" fullWidth>
                                <MenuItem value={"member"}>{roleLabel("member", category)}</MenuItem>
                                <MenuItem value={"senior"}>{roleLabel("senior", category)}</MenuItem>
                                {category !== "executive" && (
                                  <MenuItem value={"both"}>{roleLabel("both", category)}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Grid>
                    {
                        alertOpen &&
                        <Grid size={12}>
                            <Alert sx={{marginTop:"20px"}} severity={alertSeverity}>
                                {alertMsg}
                            </Alert>
                        </Grid>
                    }
                    <Grid size={12} sx={{display:"flex",justifyContent:"center"}}>
                        <Button variant="contained" onClick={submitUser}>SAVE</Button>
                        <Button variant="contained" onClick={handleCancelEdit} sx={{marginLeft:"20px"}}>CANCEL</Button>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
        <Dialog open={openDialogDelete} onClose={handleCloseDialogDelete}>
            <DialogContent>
                <Typography>Are you sure you want to DELETE this user?</Typography>
                {
                    alertOpen &&
                    <Grid size={12}>
                        <Alert sx={{marginTop:"20px"}} severity={alertSeverity}>
                            {alertMsg}
                        </Alert>
                    </Grid>
                }
                <Box sx={{display:"flex",justifyContent:"center",marginTop:"20px"}}>
                    <Button onClick={deleteUser} variant="contained" color="error">DELETE</Button>
                    <Button variant="contained" onClick={handleCloseDialogDelete} sx={{marginLeft:"20px"}}>CANCEL</Button>
                </Box>
            </DialogContent>
        </Dialog>
    </Box>
    )
}

export default TIMActions